import React, { useState } from 'react';

// The 'story' hero style (community.heroStyle === 'story'): a short story
// typed out letter by letter (or, with heroStoryEffect 'fade', faded in a
// sentence at a time) over the hero photo, typewriter-style with a
// moving cursor, in place of the one-line tagline. The pre-built page
// (scripts/generate-og-pages.js) starts typing before the app loads and
// records when in window.__heroStoryStart, so when the app takes over it
// carries on from the same letter instead of starting again.
export const HERO_STORY_FIRST_DELAY = 0.4; // seconds before the first letter
export const HERO_STORY_CHAR_STEP = 0.03; // seconds per letter (spaces included)

// 'fade' effect (community.heroStoryEffect): one sentence at a time, each
// starting once the one before has had time to be read — longer sentences
// get longer, within limits. Keep in step with scripts/generate-og-pages.js.
export const splitSentences = (text) =>
  String(text || '').trim().split(/(?<=[.?!])\s+/).filter(Boolean);
export const sentenceDelays = (sentences) => {
  let t = HERO_STORY_FIRST_DELAY;
  return sentences.map((sentence) => {
    const start = t;
    const words = sentence.split(/\s+/).length;
    t += Math.min(2, Math.max(0.9, words * 0.15));
    return start;
  });
};

const HeroStory = ({ text, effect = 'type' }) => {
  // Fixed at first render so re-renders don't restart it.
  const [elapsed] = useState(() => {
    const start = typeof window !== 'undefined' ? window.__heroStoryStart : undefined;
    return typeof start === 'number' ? (performance.now() - start) / 1000 : 0;
  });

  if (effect === 'fade') {
    const sentences = splitSentences(text);
    const delays = sentenceDelays(sentences);
    return (
      <p className="hero-story_communityHome hero-story--fade_communityHome">
        {sentences.map((sentence, i) => (
          <React.Fragment key={i}>
            <span className="hero-story-sentence_communityHome" style={{ animationDelay: `${(delays[i] - elapsed).toFixed(3)}s` }}>
              {sentence}
            </span>
            {i < sentences.length - 1 ? ' ' : ''}
          </React.Fragment>
        ))}
      </p>
    );
  }
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const lastWord = words.length - 1;

  // Each letter's delay is its position in the whole text, counting the
  // space before each word, so the typing rhythm matches a real typewriter.
  let position = 0;
  return (
    <p className="hero-story_communityHome">
      {words.map((word, w) => {
        if (w > 0) position += 1;
        const letters = [...word].map((ch, i) => {
          const delay = HERO_STORY_FIRST_DELAY + (position + i) * HERO_STORY_CHAR_STEP - elapsed;
          const isLast = w === lastWord && i === word.length - 1;
          return (
            <span
              key={i}
              className={`hero-story-char_communityHome${isLast ? ' hero-story-char--last_communityHome' : ''}`}
              style={{ animationDelay: `${delay.toFixed(3)}s` }}
            >
              {ch}
            </span>
          );
        });
        position += word.length;
        return (
          <React.Fragment key={w}>
            {/* nowrap per word so a half-typed word never splits across lines */}
            <span className="hero-story-word_communityHome">{letters}</span>
            {w < lastWord ? ' ' : ''}
          </React.Fragment>
        );
      })}
    </p>
  );
};

export default HeroStory;
